import { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import { Loader2, DollarSign, TrendingUp, Wallet } from "lucide-react";
import { get } from "@/lib/api";
import { toast } from "sonner";

const fmtMoney = (v) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(v || 0);

export default function Finances() {
    const [payments, setPayments] = useState([]);
    const [clients, setClients] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [p, c, s] = await Promise.all([
                    get("/admin/payments"),
                    get("/admin/clients"),
                    get("/admin/stats"),
                ]);
                setPayments(p);
                setClients(c);
                setStats(s);
            } catch {
                toast.error("Failed to load finances");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const clientById = Object.fromEntries(clients.map((c) => [c.id, c]));

    // Aggregate revenue by month (last 6 months)
    const months = (() => {
        const now = new Date();
        const out = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            out.push({
                key,
                label: d.toLocaleString(undefined, {
                    month: "short",
                    year: "2-digit",
                }),
                revenue: 0,
            });
        }
        for (const p of payments) {
            const k = (p.paid_at || "").slice(0, 7);
            const slot = out.find((m) => m.key === k);
            if (slot) slot.revenue += p.amount_usd || 0;
        }
        return out;
    })();

    return (
        <div data-testid="admin-finances">
            <header className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                    Money
                </p>
                <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                    Finances
                </h1>
                <p className="mt-2 text-white/55 text-sm">
                    Revenue, active client value, and payment history.
                </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <KpiCard
                    icon={DollarSign}
                    label="Total Revenue"
                    value={loading ? "—" : fmtMoney(stats?.revenue_total_usd)}
                    sub="Lifetime"
                />
                <KpiCard
                    icon={TrendingUp}
                    label="Last 30 days"
                    value={loading ? "—" : fmtMoney(stats?.revenue_30d_usd)}
                    sub="Rolling window"
                />
                <KpiCard
                    icon={Wallet}
                    label="Active Plan Value"
                    value={loading ? "—" : fmtMoney(stats?.mrr_active_usd)}
                    sub={`${stats?.clients_active || 0} active clients`}
                />
            </div>

            <section className="bg-[#060c1e] border border-[#1a2a4a] p-5 md:p-7 mb-8">
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                    Trend
                </p>
                <h2 className="mt-1 mb-4 font-display font-bold text-white text-xl">
                    Revenue · last 6 months
                </h2>
                <div className="h-72" data-testid="revenue-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={months}>
                            <CartesianGrid stroke="#142142" strokeDasharray="3 3" />
                            <XAxis dataKey="label" stroke="#6b7c97" fontSize={11} />
                            <YAxis stroke="#6b7c97" fontSize={11} />
                            <Tooltip
                                contentStyle={{
                                    background: "#020617",
                                    border: "1px solid #1a2a4a",
                                    color: "#fff",
                                    fontSize: 12,
                                }}
                                formatter={(v) => fmtMoney(v)}
                            />
                            <Bar
                                dataKey="revenue"
                                fill="#D4AF37"
                                radius={[2, 2, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <section
                className="bg-[#060c1e] border border-[#1a2a4a]"
                data-testid="payment-history"
            >
                <div className="px-5 py-3 border-b border-[#1a2a4a]">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/45">
                        Payment History
                    </p>
                </div>
                <div className="grid grid-cols-12 px-5 py-3 border-b border-[#1a2a4a] text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">
                    <div className="col-span-3">Date</div>
                    <div className="col-span-4">Client</div>
                    <div className="col-span-2">Method</div>
                    <div className="col-span-3 text-right">Amount</div>
                </div>
                {loading && (
                    <div className="px-5 py-10 text-white/55 text-sm flex items-center gap-3">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}
                {!loading && payments.length === 0 && (
                    <div className="px-5 py-10 text-white/55 text-sm">
                        No payments logged yet.
                    </div>
                )}
                {!loading &&
                    payments.map((p) => {
                        const c = clientById[p.client_id];
                        return (
                            <div
                                key={p.id}
                                className="grid grid-cols-12 px-5 py-3 items-center border-b border-[#142142] last:border-b-0 text-sm"
                            >
                                <div className="col-span-3 text-white/85 text-xs">
                                    {p.paid_at}
                                </div>
                                <div className="col-span-4 text-white">
                                    {c ? (
                                        c.name
                                    ) : (
                                        <span className="text-white/45">
                                            (deleted)
                                        </span>
                                    )}
                                    {p.note && (
                                        <p className="text-white/45 text-[11px]">
                                            {p.note}
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-2 text-white/65 text-xs uppercase tracking-[0.12em]">
                                    {p.method}
                                </div>
                                <div className="col-span-3 text-right text-[#00B7FF] font-bold">
                                    {fmtMoney(p.amount_usd)}
                                </div>
                            </div>
                        );
                    })}
            </section>
        </div>
    );
}

function KpiCard({ icon: Icon, label, value, sub }) {
    return (
        <div className="bg-[#060c1e] border border-[#1a2a4a] p-5">
            <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/55">
                    {label}
                </p>
                <Icon size={16} className="text-[#00B7FF]" />
            </div>
            <p className="font-display font-black text-white text-3xl tracking-tighter">
                {value}
            </p>
            <p className="mt-1 text-[11px] text-white/45">{sub}</p>
        </div>
    );
}
